import { AfterViewInit, Component, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { tap } from 'rxjs/operators';
import { zip } from 'rxjs';

import { AutoNumericDirective } from '../../../directives/auto-numeric.directive';
import { BasePage } from '../../base.page';
import { ConfirmDialog } from '../../../ui/dialog/confirm/confirm.dialog';
import {
    ContractDataInterface,
    ContractInterface,
    DisinvestmentInterface,
    EvaluateFeeUseCase,
    GetContractDataUseCase,
    GetContractUseCase,
    OrderType,
} from '../../../../core';
import { Disinvestment, Fee } from '../../../domain/data';
import { DisinvestmentPage } from '../disinvestment.page';
import { ErrorDialog } from '../../../ui/dialog/error/error.dialog';
import { MainRoutes } from '../../../main-routes.enum';
import { ParentEvents } from '../../../utils/parent-events.service';
import { Serializer } from '../../../client/helpers/serializer/serializer';
import { TermsAndConditionsDialog } from '../../../ui/dialog/terms-and-conditions/terms-and-conditions.dialog';

@Component({
    templateUrl: './disinvestment-operate.page.html',
    styleUrls: ['./disinvestment-operate.page.scss'],
})
export class DisinvestmentOperatePage extends BasePage implements AfterViewInit, OnInit {
    public contract: ContractInterface;
    public contractData: ContractDataInterface;
    public disinvestment: DisinvestmentInterface = new Disinvestment();

    @ViewChild('amountDirective')
    amountDirective: AutoNumericDirective;

    constructor(
        disinvestmentPage: DisinvestmentPage,
        parentEvents: ParentEvents,
        private dialog: MatDialog,
        private evaluateFeeUseCase: EvaluateFeeUseCase,
        private getContractUseCase: GetContractUseCase,
        private getContractDataUseCase: GetContractDataUseCase,
        private router: Router,
        private serializer: Serializer
    ) {
        super(parentEvents);
        disinvestmentPage.hideBackButton();
    }

    get maximumInvestment(): number {
        const balance = this.contractData?.balance || 0;
        const minimum = this.contractData?.model?.minimumInvest || 0;
        return balance - minimum;
    }

    ngOnInit(): void {
        this.getContract();
    }

    private getContract(): void {
        zip(
            this.getContractUseCase.bind().pipe(
                tap((transaction) => {
                    transaction.fold(
                        (contract) => (this.contract = contract),
                        (throwable) =>
                            this.dialog.open(ErrorDialog, {
                                data: { text: throwable.message },
                            })
                    );
                })
            ),
            this.getContractDataUseCase.bind().pipe(
                tap((transaction) => {
                    transaction.fold(
                        (data) => (this.contractData = data),
                        (throwable) =>
                            this.dialog.open(ErrorDialog, {
                                data: { text: throwable.message },
                            })
                    );
                })
            )
        ).subscribe();
    }

    onAmountChange() {
        if (this.amountDirective) {
            this.disinvestment.amount = this.amountDirective.value;
        }
    }

    showTerms() {
        this.dialog.open(TermsAndConditionsDialog, {
            data: OrderType.REFUND,
        });
    }

    onSubmit() {
        const updateAndRedirect = (changed: boolean) => {
            this.router.navigateByUrl(MainRoutes.DISINVESTMENT_SIGN, {
                state: {
                    disinvestment: this.serializer.toJson(this.disinvestment),
                    changed,
                },
            });
        };

        let fee = new Fee(this.disinvestment.amount, this.contractData.modelId);
        if (this.disinvestment.isTotal) {
            this.disinvestment.amount = this.contractData.balance;
            fee = new Fee(this.contractData.balance, this.contractData.modelId);
        }
        fee.evaluationType = 'withdrawal';

        this.evaluateFeeUseCase.bind({ data: fee }).subscribe((transaction) => {
            transaction.fold(
                (feedAmount) => {
                    if (this.contract.feedAmount !== feedAmount) {
                        this.dialog
                            .open(ConfirmDialog, {
                                data: {
                                    text: 'dialog.change-rate-disinvestment',
                                    params: { rate: feedAmount.toFixed(2) },
                                },
                            })
                            .afterClosed()
                            .subscribe((result) => {
                                if (result) {
                                    updateAndRedirect(true);
                                }
                            });
                    } else {
                        updateAndRedirect(false);
                    }
                },
                (throwable) =>
                    this.dialog.open(ErrorDialog, {
                        data: { text: throwable.message },
                    })
            );
        });
    }
}
